import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, GridColumn, GridDataStateChangeEvent, GridNoRecords } from '@progress/kendo-react-grid';
import styles from './active-services-overview.module.scss';
import { useFormatter } from '../../../hooks';
import { useNavigate } from 'react-router-dom';
import { useGridPersist } from '../../../hooks/useGridPersist';
import { useSelector } from 'react-redux';

import { Link } from 'react-router-dom';
import { State as DataState } from '@progress/kendo-data-query';

import { selectAppSettings } from '../../../features';
import { nexParkInstanceService } from '../../../services';
import { IFeaturesOverviewItem } from '../../../model/api/nexParkInstance/operations';
import { SvgIcon } from '@progress/kendo-react-common';
import { checkIcon, xIcon } from '@progress/kendo-svg-icons';

const initialDataState: DataState = {
  skip: 0,
  take: 25,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
  group: [],
};

const ActiveServicesOverview: FunctionComponent = () => {
  const formatter = useFormatter();
  const navigate = useNavigate();
  const [dataState, setDataState] = useGridPersist('features-overview-grid-state', initialDataState);
  const [gridData, setGridData] = useState<{ data: IFeaturesOverviewItem[]; total: number }>({ data: [], total: 0 });
  const [featureTitles, setFeatureTitles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const appSettings = useSelector(selectAppSettings);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    nexParkInstanceService
      .getFeaturesOverview(dataState)
      .then((result) => {
        if (isMounted) {
          setGridData({ data: result.items, total: result.total });
          setFeatureTitles(result.featureTitles);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dataState, refreshKey]);

  const onDataStateChange = useCallback(
    (event: GridDataStateChangeEvent) => {
      const newDataState = { ...event.dataState };

      if (event.targetEvent?.value === 'All') {
        newDataState.take = gridData.total;
        newDataState.skip = 0;
      }

      const hasFilterChanged = newDataState.filter !== dataState.filter;
      const hasSortChanged = newDataState.sort !== dataState.sort;

      if (hasFilterChanged || hasSortChanged) {
        newDataState.skip = 0;
      }

      setDataState(newDataState as DataState);
    },
    [dataState.filter, dataState.sort, setDataState, gridData.total],
  );

  const content = useMemo(() => {
    return (
      <div className={'page'}>
        <h3 className='page-header'>Active Services Overview</h3>

        <Grid
          className={styles.gridServices}
          data={gridData.data}
          total={gridData.total}
          skip={dataState.skip ? dataState.skip : 0}
          take={dataState.take ? dataState.take : 25}
          onDataStateChange={onDataStateChange}
          pageable={{
            buttonCount: 4,
            info: true,
            type: 'numeric',
            pageSizes: [5, 10, 25, 'All'],
            previousNext: true,
          }}
          sortable={true}
          reorderable
          resizable
        >
          <GridNoRecords>{isLoading ? 'Loading...' : 'No records available.'}</GridNoRecords>
          <GridColumn
            field='customerName'
            title='Company'
            filterable={false}
            cell={(props) => (
              <td>
                <div>
                  <Link to={`/customers/${props.dataItem.customerId}`}>{props.dataItem.customerName}</Link>
                </div>
              </td>
            )}
          />

          {featureTitles.map((featureTitle, index) => (
            <GridColumn
              key={index}
              field={`isEnabled[${index}]`}
              title={featureTitle}
              headerClassName={styles.centeredCell}
              cell={(props) => (
                <td className={styles.centeredCell}>
                  {props.dataItem.isEnabled[index] && <SvgIcon icon={checkIcon} themeColor='success'></SvgIcon>}
                  {!props.dataItem.isEnabled[index] && <SvgIcon icon={xIcon} themeColor='error'></SvgIcon>}
                </td>
              )}
            />
          ))}
        </Grid>
      </div>
    );
  }, [
    gridData.data,
    gridData.total,
    dataState.skip,
    dataState.take,
    dataState.sort,
    dataState.filter,
    onDataStateChange,
    isLoading,
    setDataState,
    navigate,
    formatter,
  ]);

  return content;
};

export default ActiveServicesOverview;
